import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Plus, X } from 'lucide-react';
import { WORK_TYPES, type CompletionPolicy, type Priority, type WorkType } from '@vwt/contracts';
import { defaultCompletionPolicy } from '@vwt/domain';
import { Button, Dialog, DialogContent, DialogFooter, Field, Input, NativeSelect, toast } from '@vwt/ui';
import { newIdempotencyKey, useApiMutation, useApiQuery } from '../../lib/api.js';
import { fieldErrors, toastError } from '../../lib/errors.js';
import { currentLocale, useTranslation } from '../../lib/i18n.js';
import { useMe } from '../../lib/session.js';
import { MarkdownEditor } from '../../components/markdown.js';
import { PrincipalPicker } from '../../components/common.js';
import { SaveTemplateDialog, TemplatePicker } from '../../components/templates.js';
import { SimilarWork } from '../../components/similar.js';

const TYPES = WORK_TYPES;

/** Few-step creation; no repository, sprint or estimate required (Section 1). */
export function CreateWorkDialog({ open, onOpenChange, defaults }: { open: boolean; onOpenChange: (open: boolean) => void; defaults?: { projectId?: string; teamId?: string; requestId?: string; title?: string; parentId?: string; parentKey?: string; type?: WorkType; priority?: Priority; labels?: string[]; descriptionMarkdown?: string } }) {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const teams = useApiQuery('listTeams', { query: {} }, { enabled: open });
  const projects = useApiQuery('listProjects', { query: {} }, { enabled: open });
  const create = useApiMutation('createWorkItem', { invalidate: ['listWorkItems', 'getWorkItem', 'getRequest', 'getRequestWork'] });
  const [idempotencyKey, setKey] = useState(newIdempotencyKey);
  const [title, setTitle] = useState(defaults?.title ?? '');
  const [type, setType] = useState<WorkType>(defaults?.type ?? (me.permissions.canCreateDevelopmentWork ? 'FEATURE' : 'OPERATIONS'));
  const [responsibility, setResponsibility] = useState<string>(defaults?.teamId ?? me.teams[0]?.id ?? 'personal');
  const [projectId, setProjectId] = useState(defaults?.projectId ?? '');
  const [policy, setPolicy] = useState<CompletionPolicy | ''>('');
  const [priority, setPriority] = useState<Priority>(defaults?.priority ?? 'NORMAL');
  const [description, setDescription] = useState(defaults?.descriptionMarkdown ?? '');
  const [criteria, setCriteria] = useState<string[]>([]);
  const [assignee, setAssignee] = useState<{ id: string; name: string } | null>(null);
  const [labels, setLabels] = useState((defaults?.labels ?? []).join(', '));
  const [subItems, setSubItems] = useState<Array<{ title: string; type?: WorkType | undefined }>>([]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setKey(newIdempotencyKey());
      setErrors({});
    }
  }, [open]);

  const project = projects.data?.data.find((p) => p.id === projectId);
  const effectivePolicy = policy || defaultCompletionPolicy(type, project?.requiresReview ?? false);

  const submit = async () => {
    try {
      const result = await create.mutateAsync({
        idempotencyKey,
        body: {
          title: title.trim(),
          type,
          completionPolicy: effectivePolicy,
          responsibility: responsibility === 'personal' ? { kind: 'personal', personalOwnerId: me.id } : { kind: 'team', teamId: responsibility },
          originalLocale: currentLocale(),
          priority,
          ...(projectId ? { projectId } : {}),
          ...(description.trim() ? { descriptionMarkdown: description } : {}),
          ...(criteria.filter((c) => c.trim()).length ? { acceptanceCriteria: criteria.filter((c) => c.trim()).map((text) => ({ text })) } : {}),
          ...(assignee ? { assigneeId: assignee.id } : {}),
          ...(labels.trim() ? { labels: labels.split(',').map((l) => l.trim()).filter(Boolean) } : {}),
          ...(defaults?.requestId ? { requestId: defaults.requestId } : {}),
          ...(defaults?.parentId ? { parentId: defaults.parentId } : {}),
          ...(subItems.length ? { subItems: subItems.map((s) => ({ title: s.title, ...(s.type ? { type: s.type } : {}) })) } : {}),
          ...(startDate ? { startDate } : {}),
          ...(dueDate ? { dueDate } : {}),
        },
      });
      toast.success(t('work.create.created', { key: result!.data.key }));
      onOpenChange(false);
      navigate(`/work?selected=${result!.data.key}`);
    } catch (error) {
      setErrors(fieldErrors(error));
      toastError(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={t('work.create.title')} description={defaults?.parentKey ? t('work.detail.subItemOf', { key: defaults.parentKey }) : undefined} className="w-[min(680px,calc(100vw-2rem))]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              void submit();
            }
          }}
          className="flex flex-col gap-4"
        >
          <TemplatePicker
            kind="WORK"
            projectId={projectId || undefined}
            onApply={(p) => {
              if (p.title) setTitle(p.title);
              if (p.type) setType(p.type);
              if (p.priority) setPriority(p.priority);
              // Only what the template defines replaces the form; everything else is kept.
              if (p.descriptionMarkdown !== undefined) setDescription(p.descriptionMarkdown);
              if (p.acceptanceCriteria !== undefined) setCriteria(p.acceptanceCriteria);
              if (p.labels !== undefined) setLabels(p.labels.join(', '));
              setSubItems(p.subItems ?? []);
            }}
          />
          <Field label={t('intake.fields.title')} htmlFor="work-title" required error={errors.title}>
            <Input id="work-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={240} autoFocus required />
          </Field>
          {open ? <SimilarWork kind="WORK" title={title} description={description} /> : null}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label={t('work.create.type')} htmlFor="work-type">
              <NativeSelect id="work-type" value={type} onChange={(e) => setType(e.target.value as WorkType)}>
                {TYPES.map((v) => (
                  <option key={v} value={v}>
                    {t(`status.workType.${v}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('work.create.responsibility')} htmlFor="work-resp">
              <NativeSelect id="work-resp" value={responsibility} onChange={(e) => setResponsibility(e.target.value)}>
                <option value="personal">{t('work.create.personal')}</option>
                {(teams.data?.data ?? []).map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('work.create.project')} htmlFor="work-project">
              <NativeSelect id="work-project" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                <option value="">{t('work.create.noProject')}</option>
                {(projects.data?.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.key} · {p.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Field label={t('work.create.policy')} htmlFor="work-policy" hint={t(`status.policyHelp.${effectivePolicy}`)}>
              <NativeSelect id="work-policy" value={policy} onChange={(e) => setPolicy(e.target.value as CompletionPolicy)}>
                <option value="">{t(`status.policy.${effectivePolicy}`)} ✓</option>
                {(['PRODUCTION', 'DELIVERABLE', 'REVIEWED_DELIVERABLE'] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`status.policy.${p}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('intake.fields.priority')} htmlFor="work-priority">
              <NativeSelect id="work-priority" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`status.priority.${p}`)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label={t('work.create.assignee')}>
              <PrincipalPicker label={t('work.create.assignee')} value={assignee} onChange={(p) => setAssignee(p ? { id: p.id, name: p.name } : null)} />
            </Field>
          </div>
          <Field label={t('work.create.description')}>
            <MarkdownEditor value={description} onChange={setDescription} minRows={4} />
          </Field>
          <Field label={t('work.create.criteria')}>
            <ul className="flex flex-col gap-1.5">
              {criteria.map((c, i) => (
                <li key={i} className="flex gap-2">
                  <Input value={c} onChange={(e) => setCriteria((list) => list.map((x, j) => (j === i ? e.target.value : x)))} placeholder={t('work.create.criterionPlaceholder')} aria-label={`${t('work.create.criteria')} ${i + 1}`} />
                  <Button type="button" variant="ghost" size="icon" aria-label={t('common.delete')} onClick={() => setCriteria((list) => list.filter((_, j) => j !== i))}>
                    <X />
                  </Button>
                </li>
              ))}
            </ul>
            <Button type="button" variant="ghost" size="sm" className="self-start" onClick={() => setCriteria((list) => [...list, ''])}>
              <Plus /> {t('work.create.addCriterion')}
            </Button>
          </Field>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
            <Field label={t('work.create.labels')} htmlFor="work-labels">
              <Input id="work-labels" value={labels} onChange={(e) => setLabels(e.target.value)} placeholder={t('work.create.labelsPlaceholder')} />
            </Field>
            <Field label={t('work.create.startDate')} htmlFor="work-start" error={errors.startDate}>
              <Input id="work-start" type="date" value={startDate} max={dueDate || undefined} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label={t('work.create.dueDate')} htmlFor="work-due">
              <Input id="work-due" type="date" value={dueDate} min={startDate || undefined} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          </div>
          {subItems.length ? (
            <Field label={t('templates.subItems', { count: subItems.length })}>
              <ul className="flex flex-col gap-1">
                {subItems.map((s, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <span className="min-w-0 flex-1 truncate">{s.title}</span>
                    {s.type ? <span className="text-xs text-fg-subtle">{t(`status.workType.${s.type}`)}</span> : null}
                    <Button type="button" variant="ghost" size="iconSm" aria-label={t('common.removeItem', { name: s.title })} onClick={() => setSubItems((list) => list.filter((_, j) => j !== i))}>
                      <X />
                    </Button>
                  </li>
                ))}
              </ul>
            </Field>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" className="mr-auto" onClick={() => setSavingTemplate(true)} disabled={!title.trim() && !description.trim()}>
              {t('templates.saveAs')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={create.isPending} disabled={!title.trim()}>
              {t('common.create')}
            </Button>
          </DialogFooter>
          {savingTemplate ? (
            <SaveTemplateDialog
              kind="WORK"
              defaultName={title.trim()}
              projectId={projectId || null}
              onClose={() => setSavingTemplate(false)}
              payload={{
                ...(title.trim() ? { title: title.trim() } : {}),
                type,
                priority,
                ...(description.trim() ? { descriptionMarkdown: description } : {}),
                ...(criteria.filter((c) => c.trim()).length ? { acceptanceCriteria: criteria.filter((c) => c.trim()) } : {}),
                ...(labels.trim() ? { labels: labels.split(',').map((l) => l.trim()).filter(Boolean) } : {}),
                ...(subItems.length ? { subItems } : {}),
              }}
            />
          ) : null}
        </form>
      </DialogContent>
    </Dialog>
  );
}
